import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  GraduationCap,
  Home,
  LogIn,
  Package,
  Phone,
  ShoppingBag,
  Users,
  X,
  Menu,
  Globe2,
} from 'lucide-react';
import { lmsHref } from '../lib/surface';
import logo from './assets/logo.png';

const primaryLinks = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Short Courses', href: '/short-courses', icon: ShoppingBag },
  { name: 'Contact', href: '/contact', icon: Phone },
];

const programLinks = [
  {
    name: 'Foodstuff Export Masterclass',
    href: '/fem',
    description: 'Build and scale a foodstuff export business.',
    meta: 'Masterclass',
    icon: Package,
  },
  {
    name: 'Zero2Export',
    href: '/zero2export',
    description: 'A two-day intensive from beginner to exporter.',
    meta: '2-day intensive',
    icon: Globe2,
  },
  {
    name: 'Export Mentorship Program',
    href: '/emp',
    description: 'Three months of guided export mentorship.',
    meta: 'Mentorship',
    icon: Users,
  },
  {
    name: 'Campus2Container',
    href: '/campus2container',
    description: 'A free six-week pathway for students and NYSC.',
    meta: 'Free',
    icon: GraduationCap,
  },
];

const linkBase =
  'inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[13.5px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const programsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isActive = (href: string) => {
    const path = location.pathname;
    if (href === '/') return path === '/';
    if (href === '/courses') return path === '/courses' || path.startsWith('/course/');
    if (href === '/short-courses') return path === '/short-courses' || path === '/products';
    return path === href || path.startsWith(`${href}/`);
  };

  const programsActive = programLinks.some((program) => isActive(program.href));
  const leftNavLinks = primaryLinks.slice(0, 1);
  const rightNavLinks = primaryLinks.slice(1);

  const renderNavLink = (link: (typeof primaryLinks)[number]) => {
    const active = isActive(link.href);
    return (
      <Link
        key={link.name}
        to={link.href}
        aria-current={active ? 'page' : undefined}
        className={`${linkBase} ${
          active
            ? 'bg-blue-50/80 text-blue-700'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`}
      >
        {link.name}
      </Link>
    );
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (programsRef.current && !programsRef.current.contains(event.target as Node)) {
        setProgramsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setProgramsOpen(false);
      setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setProgramsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.dataset.mobileNav = 'open';
    return () => {
      document.body.style.overflow = previous;
      delete document.body.dataset.mobileNav;
    };
  }, [isOpen]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) setIsOpen(false);
    };
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, []);

  const closeMobile = () => setIsOpen(false);

  return (
    <>
      {isOpen && (
        <div
          aria-hidden="true"
          onClick={closeMobile}
          className="nav-fade fixed inset-x-0 bottom-0 top-20 z-40 cursor-pointer bg-slate-900/25 backdrop-blur-[2px] lg:hidden"
        />
      )}

      <header className="fixed inset-x-0 top-0 z-50">
        <div className="h-[3px] bg-gradient-to-r from-[#1f2b5b] via-blue-600 to-[#b99a5b]" />

        <nav
          aria-label="Main navigation"
          className={`border-b bg-white transition-shadow duration-300 ${
            scrolled
              ? 'border-slate-200/80 shadow-[0_10px_35px_-18px_rgba(15,23,42,0.45)]'
              : 'border-slate-200/60'
          }`}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-[77px] items-center justify-between gap-4">
              <Link
                to="/"
                onClick={closeMobile}
                aria-label="Lagos School of Export Management — home"
                className="group flex flex-none items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2"
              >
                <img
                  src={logo}
                  alt=""
                  className="h-11 w-11 object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                />
                <span className="flex flex-col leading-none">
                  <span className="text-[17px] font-extrabold tracking-[0.2em] text-slate-900">
                    LSEM
                  </span>
                  <span className="mt-1.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Lagos · Nigeria
                  </span>
                </span>
              </Link>

              <div className="hidden flex-1 items-center justify-center lg:flex">
                <div className="flex items-center gap-0.5">
                  {leftNavLinks.map(renderNavLink)}

                  <div
                    ref={programsRef}
                    className="relative"
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                        setProgramsOpen(false);
                      }
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setProgramsOpen((open) => !open)}
                      aria-expanded={programsOpen}
                      aria-controls="programs-menu"
                      className={`${linkBase} ${
                        programsActive || programsOpen
                          ? 'bg-blue-50/80 text-blue-700'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      Programs
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${
                          programsOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {programsOpen && (
                      <div className="absolute left-1/2 top-full z-50 w-[390px] -translate-x-1/2 pt-3">
                        <div
                          id="programs-menu"
                          role="group"
                          aria-label="Programs"
                          className="nav-pop overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-2 shadow-[0_24px_70px_-24px_rgba(15,23,42,0.45)]"
                        >
                          <div className="px-3 pb-2 pt-2">
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                              Scale-up programs
                            </span>
                          </div>

                          <div className="space-y-0.5">
                            {programLinks.map((program) => {
                              const active = isActive(program.href);
                              return (
                                <Link
                                  key={program.name}
                                  to={program.href}
                                  onClick={() => setProgramsOpen(false)}
                                  aria-current={active ? 'page' : undefined}
                                  className={`group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/40 ${
                                    active ? 'bg-blue-50/60' : 'hover:bg-slate-50'
                                  }`}
                                >
                                  <span
                                    className={`mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-lg border transition-colors duration-150 ${
                                      active
                                        ? 'border-blue-200 bg-white text-blue-600'
                                        : 'border-slate-200/80 bg-slate-50 text-slate-500 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:text-blue-600'
                                    }`}
                                  >
                                    <program.icon className="h-4 w-4" />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="flex items-center gap-2">
                                      <span
                                        className={`text-[13px] font-semibold ${
                                          active ? 'text-blue-700' : 'text-slate-900'
                                        }`}
                                      >
                                        {program.name}
                                      </span>
                                      {program.meta === 'Free' && (
                                        <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                                          Free
                                        </span>
                                      )}
                                    </span>
                                    <span className="mt-0.5 block text-[11.5px] leading-snug text-slate-500">
                                      {program.description}
                                    </span>
                                  </span>
                                  <ArrowRight className="mt-1 h-3.5 w-3.5 flex-none text-slate-300 opacity-0 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-blue-500 group-hover:opacity-100" />
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {rightNavLinks.map(renderNavLink)}
                </div>
              </div>

              <div className="hidden flex-none items-center gap-2.5 lg:flex">
                <a
                  href={lmsHref('/login')}
                  className="inline-flex h-10 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold text-slate-600 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2"
                >
                  <LogIn className="h-3.5 w-3.5" />
                  Student login
                </a>
                <Link
                  to="/courses"
                  className="group inline-flex h-10 items-center gap-2 rounded-full bg-blue-600 px-4 text-[13px] font-semibold text-white shadow-[0_7px_18px_-8px_rgba(37,99,235,0.75)] transition-all duration-200 hover:bg-blue-700 hover:shadow-[0_9px_22px_-8px_rgba(37,99,235,0.85)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-2"
                >
                  Start a Course
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen((open) => !open)}
                aria-expanded={isOpen}
                aria-controls="mobile-menu"
                aria-label={isOpen ? 'Close menu' : 'Open menu'}
                className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 lg:hidden"
              >
                {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {isOpen && (
            <div
              id="mobile-menu"
              className="nav-pop max-h-[calc(100dvh-5rem)] overflow-y-auto border-t border-slate-100 bg-white px-4 pb-8 pt-5 shadow-[0_24px_50px_-24px_rgba(15,23,42,0.35)] sm:px-6 lg:hidden"
            >
              <p className="px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Explore
              </p>
              <div className="mt-2 space-y-0.5">
                {primaryLinks.map((link) => {
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={closeMobile}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/40 ${
                        active
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg ${
                          active ? 'bg-white text-blue-600' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        <link.icon className="h-4 w-4" />
                      </span>
                      {link.name}
                      {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-blue-600" />}
                    </Link>
                  );
                })}
              </div>

              <div className="my-5 h-px bg-slate-100" />

              <p className="px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Scale-up programs
              </p>
              <div className="mt-2 space-y-0.5">
                {programLinks.map((program) => {
                  const active = isActive(program.href);
                  return (
                    <Link
                      key={program.name}
                      to={program.href}
                      onClick={closeMobile}
                      aria-current={active ? 'page' : undefined}
                      className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/40 ${
                        active ? 'bg-blue-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span
                        className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg transition-colors ${
                          active
                            ? 'bg-white text-blue-600'
                            : 'bg-slate-100 text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600'
                        }`}
                      >
                        <program.icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={`flex items-center gap-2 text-[13.5px] font-semibold ${
                            active ? 'text-blue-700' : 'text-slate-800'
                          }`}
                        >
                          {program.name}
                          {program.meta === 'Free' && (
                            <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                              Free
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-slate-500">
                          {program.description}
                        </span>
                      </span>
                    </Link>
                  );
                })}
              </div>

              <div className="my-5 h-px bg-slate-100" />

              <div className="grid grid-cols-2 gap-2.5">
                <a
                  href={lmsHref('/login')}
                  onClick={closeMobile}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                >
                  <LogIn className="h-4 w-4" />
                  Student login
                </a>
                <Link
                  to="/courses"
                  onClick={closeMobile}
                  className="flex h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                >
                  Start a course
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>
    </>
  );
};

export default Navbar;
